"use client"
import {
  Flex,
  FlexCenter,
  FlexEnd,
  Input,
  ItemAvatar,
  ItemContent,
  ItemTitle,
  Media,
  NumberDisplay,
  QuantityBadge,
  QuantityInput,
  SkeletonBlock,
  SkeletonLine,
  SpaceBetween,
  TABLE_SIZES,
  TableCell,
  TableRow,
  TextBody,
  TextOverflowTooltip,
  UnstyledButton,
  useTable,
} from "@opensea/ui-kit"
import { Close } from "@opensea/ui-kit/icons"
import { bn } from "@opensea/utils"
import type { FragmentOf } from "gql.tada"
import { readFragment } from "gql.tada"
import {
  Controller,
  useFieldArray,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form"
import { useTranslations } from "use-intl"
import { Item } from "../../../../../components/Item"
import { RarityBadge } from "../../../../../components/RarityBadge"
import { useSyncFormFields } from "../../../../../hooks/useSyncFormField"
import { calculateOrderTotals } from "../../../../../lib/fees"
import {
  LISTING_MAX_DECIMALS,
  MINIMUM_LISTING_PRICE,
  ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
  ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE,
} from "../../../constants"
import type { ListingFlowFormValues } from "../../types"
import { LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES } from "./constants"
import { ListingFlowItemsTableRowFragment } from "./ListingFlowItemsTableRow.fragment"
import { readItemHighestTraitFloor } from "./readItemHighestTraitFloor"

type Props = {
  item: FragmentOf<typeof ListingFlowItemsTableRowFragment> | undefined
  index: number
  context?: {
    isReview: boolean
  }
}

export const ListingFlowItemsTableRow = ({
  item: itemKey,
  index,
  context,
}: Props) => {
  const t = useTranslations("wallet.ListingFlowItemsTableRow")
  const item = readFragment(ListingFlowItemsTableRowFragment, itemKey)

  const { register, control, setValue } =
    useFormContext<ListingFlowFormValues>()

  const { errors } = useFormState({ control })

  const { remove } = useFieldArray({
    keyName: "_id",
    control,
    name: "listings",
  })

  const isReview = context?.isReview ?? false
  const listings = useWatch({ control, name: "listings" })
  const adjustForFees = useWatch({ control, name: "adjustForFees" })
  const fieldItem = listings[index]
  const error = errors.listings?.[index]?.price
  const quantityError = errors.listings?.[index]?.quantity
  const { adjustedForFees: totalAdjustedForFees, afterFees } =
    calculateOrderTotals([fieldItem])

  const { size } = useTable()
  const imageSize = TABLE_SIZES[size].image
  const isFungible = item?.isFungible

  const floorPrice = isFungible
    ? item.bestListing?.pricePerItem.token
    : item?.collection?.floorPrice?.pricePerItem.token
  const priceWarning =
    floorPrice &&
    fieldItem.price > 0 &&
    bn(fieldItem.price).lt(
      bn(floorPrice.unit)
        .times(1 - ORDER_PRICE_LOW_SEVERITY_WARNING_THRESHOLD_PERCENTAGE)
        .decimalPlaces(LISTING_MAX_DECIMALS),
    )
      ? t("priceBelowFloor", {
          price: bn(floorPrice.unit)
            .decimalPlaces(LISTING_MAX_DECIMALS)
            .toString(),
          symbol: floorPrice.symbol,
        })
      : undefined
  const showHighSevPriceWarning =
    floorPrice &&
    Boolean(priceWarning) &&
    bn(fieldItem.price).lt(
      bn(floorPrice.unit)
        .times(1 - ORDER_PRICE_HIGH_SEVERITY_WARNING_THRESHOLD_PERCENTAGE)
        .decimalPlaces(LISTING_MAX_DECIMALS),
    )

  const quantityErrorContent = quantityError && (
    <TextBody color="error-1" size="xs">
      {quantityError.message || t("insufficientQuantity")}
    </TextBody>
  )

  const priceErrorContent = fieldItem.price !== 0 &&
    ((priceWarning && !isReview) || error) && (
      <TextBody
        color={
          priceWarning && !showHighSevPriceWarning && !error
            ? "warning"
            : "error-1"
        }
        size="xs"
      >
        {error ? error.message : priceWarning}
      </TextBody>
    )

  const quantityContent = isFungible ? (
    isReview ? (
      <QuantityBadge className="ml-1">{fieldItem.quantity}</QuantityBadge>
    ) : (
      <Controller
        control={control}
        name={`listings.${index}.quantity`}
        render={({ field }) => (
          <QuantityInput
            className="ml-1 min-w-[80px]"
            error={Boolean(quantityError)}
            min={0}
            onValueChange={field.onChange}
            size="sm"
            value={field.value}
          />
        )}
      />
    )
  ) : null

  useSyncFormFields({
    fields: [
      {
        name: `listings.${index}.shouldConfirmPrice`,
        value: showHighSevPriceWarning,
      },
    ],
    setValue,
  })

  if (!item) {
    return <ListingFlowItemsTableRowSkeleton />
  }

  const listingSymbol = item.chain.listingCurrency.symbol

  const itemLockup = (
    <Item variant="unstyled">
      <ItemAvatar className="rounded" size={imageSize} src={item.imageUrl} />
      <ItemContent>
        <FlexCenter className="w-full min-w-0 gap-2">
          <TextOverflowTooltip>
            <ItemTitle>{item.name || `#${item.tokenId}`}</ItemTitle>
          </TextOverflowTooltip>
          <RarityBadge item={item} />
          <Media greaterThanOrEqual="md">{quantityContent}</Media>
        </FlexCenter>
      </ItemContent>
    </Item>
  )

  const priceContent = isReview ? (
    <TextOverflowTooltip>
      <TextBody>
        <NumberDisplay suffix={listingSymbol} value={fieldItem.price} />
      </TextBody>
    </TextOverflowTooltip>
  ) : (
    <Input
      aria-label="Listing price"
      autoFocus={index === 0}
      className="font-mono"
      disabled={isReview}
      endEnhancer={listingSymbol}
      error={
        fieldItem.price !== 0 &&
        (Boolean(error?.message) || showHighSevPriceWarning)
      }
      min={0}
      placeholder="0"
      size="sm"
      step={MINIMUM_LISTING_PRICE}
      type="number"
      warning={Boolean(priceWarning)}
      {...register(`listings.${index}.price`, {
        setValueAs: value => (value ? Number.parseFloat(value) : undefined),
      })}
    />
  )

  const showDeleteCell = listings.length > 1 && !isReview
  const deleteButton = (
    <UnstyledButton
      onClick={() => {
        remove(index)
      }}
    >
      <Close fill="interactive-primary-styles" size={20} />
    </UnstyledButton>
  )

  return (
    <>
      <Media lessThan="md">
        <TableRow>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}>
            {itemLockup}
          </TableCell>

          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>
            {isFungible ? quantityContent : priceContent}
          </TableCell>
          {showDeleteCell && (
            <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}>
              {deleteButton}
            </TableCell>
          )}
        </TableRow>

        {quantityErrorContent}

        {isFungible && (
          <TableRow>
            <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}>
              <TextBody weight="semibold">{t("itemPrice")}</TextBody>
            </TableCell>

            <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>
              {priceContent}
            </TableCell>
            {showDeleteCell && (
              <TableCell
                className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}
              />
            )}
          </TableRow>
        )}

        <FlexEnd>{priceErrorContent}</FlexEnd>
      </Media>
      <Media greaterThanOrEqual="md">
        <TableRow>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}>
            {itemLockup}
          </TableCell>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.floor}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={floorPrice?.symbol}
                  value={floorPrice?.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell
            className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.traitFloor}
          >
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={readItemHighestTraitFloor(item)?.token.symbol}
                  value={readItemHighestTraitFloor(item)?.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.topOffer}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={item.bestOffer?.pricePerItem.token.symbol}
                  value={item.bestOffer?.pricePerItem.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.cost}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={item.lastSale?.token.symbol}
                  value={item.lastSale?.token.unit}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.proceeds}>
            <TextOverflowTooltip>
              <TextBody>
                <NumberDisplay
                  suffix={listingSymbol}
                  value={adjustForFees ? totalAdjustedForFees : afterFees}
                />
              </TextBody>
            </TextOverflowTooltip>
          </TableCell>
          <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>
            {priceContent}
          </TableCell>
          {showDeleteCell && (
            <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}>
              {deleteButton}
            </TableCell>
          )}
        </TableRow>
        {(Boolean(priceErrorContent) || quantityError) && (
          <SpaceBetween className="w-full">
            <div>{quantityErrorContent}</div>
            <div>{priceErrorContent}</div>
          </SpaceBetween>
        )}
      </Media>
    </>
  )
}

export const ListingFlowItemsTableRowSkeleton = (context: {
  context?: {
    count: number
  }
}) => {
  const { size } = useTable()
  const imageSize = TABLE_SIZES[size].image
  const numItems = context.context?.count ?? 1

  return (
    <TableRow>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.item}>
        <Item variant="unstyled">
          <ItemAvatar className="rounded" size={imageSize}>
            <SkeletonBlock />
          </ItemAvatar>
          <ItemContent>
            <Flex className="gap-2">
              <SkeletonLine className="h-4 w-16" />
            </Flex>
          </ItemContent>
        </Item>
      </TableCell>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.floor}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.traitFloor}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.topOffer}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.cost}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.proceeds}>
        <SkeletonLine className="h-4 w-20" />
      </TableCell>
      <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.price}>
        <SkeletonLine className="h-8 w-full" />
      </TableCell>
      {numItems > 1 && (
        <TableCell className={LISTINGS_FLOW_ITEMS_TABLE_CLASSNAMES.remove}>
          <SkeletonBlock className="size-5" />
        </TableCell>
      )}
    </TableRow>
  )
}
